import React from "react";
import AdSenseBanner from "../components/AdSenseBanner";
import Obd2ScannerCard from "../components/Obd2ScannerCard";

function Tutorial() {
  return (
    <article
      className="container tutorial-page"
      style={{ paddingTop: "80px" }}
    >
      <header>
        <h1 className="mb-4">
          Tutorial Completo de Programação ETACS (Lancer, ASX, Outlander,
          Pajero, L200) com MMCodingWriter
        </h1>
      </header>

      <AdSenseBanner />

      <p>
        Se você tem um veículo Mitsubishi (Lancer, ASX, Outlander, Pajero, L200
        etc.) e deseja ativar ou personalizar diversas funcionalidades na
        central ETACS – como travamento automático das portas, subida de vidros
        pelo controle, ajustes de faróis e muito mais –, este guia completo vai
        lhe mostrar como fazer isso utilizando o software{" "}
        <strong>MMCodingWriter</strong>. Confira abaixo todo o processo, desde
        os cuidados iniciais até a gravação final das configurações.
      </p>

      <section>
        <h2>1. Avisos e Cuidados Importantes</h2>
        <ol>
          <li>
            <strong>Risco de perda de dados:</strong> Toda alteração na central
            ETACS deve ser feita com cautela. É fundamental fazer backup (salvar
            os arquivos originais – Variant e Custom) antes de qualquer
            modificação.
          </li>
          <li>
            <strong>Responsabilidade:</strong> As instruções abaixo são fruto de
            experiências e pesquisas de entusiastas. Faça por sua conta e risco.
            Em caso de erro, apenas a concessionária ou alguém com o software
            original de fábrica poderá recuperar sua ETACS se você não tiver
            backups.
          </li>
          <li>
            <strong>Compatibilidade:</strong>
            <br />
            - A maioria dos exemplos neste guia refere-se a veículos Mitsubishi
            Lancer, ASX, Outlander e Pajero/L200 de certos anos (2007+).
            <br />
            - As funções e parâmetros podem variar conforme o ano/modelo/versão
            do veículo.
            <br />- Nem todas as funções mostradas estarão disponíveis em todos
            os modelos.
          </li>
        </ol>
      </section>

      <section>
        <h2>2. Materiais Necessários</h2>
        <Obd2ScannerCard />
        <ol>
          <li>
            <strong>Notebook ou PC com Windows</strong> (preferencialmente
            Windows 10).
            <br />
            Em Windows 8 ou 11 também pode funcionar, mas o Windows 10 costuma
            ter menos problemas de driver/compatibilidade.
          </li>
          <li>
            <strong>Interface OBD2</strong>. Existem dois tipos principais que
            costumam ser usados:
            <ul>
              <li>
                <em>ELM327 v1.5 (Bluetooth ou USB)</em>.
                <br />
                Importante: verifique se é a versão 1.5 real, com chipset
                PIC18F25k80. Versões falsas (v2.1, por ex.) nem sempre
                funcionam.
              </li>
              <li>
                <em>Cabo mini VCI J2534</em> (geralmente usado também em
                Toyota).
                <br />
                Normalmente encontrado em sites de importação.
              </li>
            </ul>
          </li>
          <li>
            <strong>Software MMCodingWriter</strong>.
            <br />
            Existem versões 2.1 e 2.3 (ou mais recentes). Em muitos casos, a
            versão 2.3 tem sido a mais compatível com Lancer/ASX/Outlander a
            partir de 2013+. Para baixar: em geral está disponível em fóruns
            internacionais da Mitsubishi ou em links de comunidades.
          </li>
          <li>
            (Opcional) <strong>Driver específico do cabo ou interface</strong>.
            <br />
            - Se for ELM327 Bluetooth, geralmente basta parear no Windows.
            <br />- Se for o mini VCI, instale o driver do cabo (normalmente
            acompanha no CD ou link do vendedor).
          </li>
        </ol>
      </section>

      <section>
        <h2>3. Preparando o Ambiente (ELM327 Bluetooth)</h2>
        <p>
          (Se você estiver usando o cabo mini VCI J2534, pule para a seção de
          conexão do MMCodingWriter; a lógica de salvamento e escrita é a mesma.
          Só certifique-se de instalar o driver do cabo e apontá-lo corretamente
          no MMCodingWriter.)
        </p>
        <ol>
          <li>
            Ligar o carro na posição de ignição (sem dar partida, apenas “ligar
            o painel”).
          </li>
          <li>
            Conectar o ELM327 na porta OBD2 do seu Mitsubishi.
            <br />
            A porta OBD2 geralmente fica abaixo do volante, na região dos pedais
            ou um pouco lateral.
            <br />
            Certifique-se de que a chave do carro esteja desligada ao encaixar;
            depois ligue a ignição.
          </li>
          <li>
            Parear via Bluetooth no Windows:
            <ul>
              <li>
                No Windows, vá em{" "}
                <em>
                  Configurações → Bluetooth e Dispositivos → Adicionar
                  dispositivo
                </em>{" "}
                → Selecione “Bluetooth”.
              </li>
              <li>Procure o dispositivo nomeado “OBD2” ou “Bluetooth OBD”.</li>
              <li>Se pedir senha/pin, use “1234” ou “0000”.</li>
              <li>Aguarde a confirmação de emparelhamento.</li>
            </ul>
          </li>
          <li>
            Configurar a Porta COM:
            <ul>
              <li>
                Abra o Gerenciador de Dispositivos (busque por “Gerenciador de
                Dispositivos” ou <em>devmgmt.msc</em>).
              </li>
              <li>Expanda a seção “Portas (COM e LPT)”.</li>
              <li>
                Você verá algo como “Bluetooth Serial Port (COMx)” (pode haver 2
                portas listadas).
              </li>
              <li>
                Clique duas vezes em cada “COMx”, vá em{" "}
                <em>Configurações de Porta</em> → <em>Bits por segundo</em> e
                selecione 38400. Dê OK.
              </li>
              <li>Repita em todas as portas criadas para o ELM.</li>
            </ul>
          </li>
          <li>
            Desativar Antivirus/Firewall (caso necessário):
            <br />
            Alguns antivírus bloqueiam o arquivo do MMCodingWriter por detectar
            como “falso positivo”.
            <br />
            Desative momentaneamente para conseguir abrir o software.
          </li>
        </ol>
      </section>

      <section>
        <h2>4. Configurando o MMCodingWriter</h2>
        <ol>
          <li>
            Abra o MMCodingWriter (geralmente não requer instalação, basta
            executar o .exe).
          </li>
          <li>
            Se aparecer aviso de versão mais recente, ignore por enquanto (caso
            tenha a 2.3, ela já tende a funcionar bem).
          </li>
          <li>
            Vá em <em>Settings</em> (ou “Configurações”):
            <ul>
              <li>Adapter: selecione ELM327 (ou J2534, se for seu caso).</li>
              <li>
                Port: escolha a porta COM onde você configurou 38400. (Ex.:
                COM3, COM6, etc.)
              </li>
              <li>
                Baud Rate: 38400 (se o “Auto Baud” não estiver funcionando,
                deixe fixo em 38400).
              </li>
              <li>
                Marque a caixa “Write Bypass” se disponível (algumas versões
                aparece como “White Bypass” ou “Bypass Write”).
              </li>
              <li>
                Desmarque outros módulos se quiser uma conexão mais rápida (no
                início, é comum deixar só “ETACS” marcado).
              </li>
            </ul>
          </li>
          <li>
            Clique em <em>Connect</em>:
            <br />
            Se tudo der certo, aparecerá algo como “ETACS found” ou similar.
            <br />
            Se ficar muito tempo sem resposta ou mostrar erro, feche o programa,
            desligue a ignição, conecte novamente e tente outra vez (algumas
            interfaces podem falhar na primeira tentativa).
          </li>
        </ol>
      </section>

      <section>
        <h2>5. Salvando o Arquivo Original (Backup)</h2>
        <p>
          Antes de qualquer modificação, salve <strong>SEMPRE</strong> o estado
          original.
          <br />
          No MMCodingWriter, você verá duas abas principais quando estiver em
          ETACS:
        </p>
        <ul>
          <li>
            <strong>Variants</strong>
          </li>
          <li>
            <strong>Custom</strong>
          </li>
        </ul>
        <p>
          Cada uma tem seus parâmetros. Você precisa salvar os dois arquivos de
          backup:
        </p>
        <ol>
          <li>
            Clique em “Variants” → “Save” (ou “Read” e depois “Save”).
            <br />
            Escolha uma pasta no seu PC e salve como{" "}
            <em>Variants_original.mcf</em> (ou nome similar).
          </li>
          <li>
            Clique em “Custom” → “Save” (ou “Read” e depois “Save”).
            <br />
            Salve como <em>Custom_original.mcf</em>.
          </li>
        </ol>
        <p>
          Armazene-os em local seguro. Caso algo dê errado, basta reabrir estes
          arquivos originais e clicar em “Write” para restaurar tudo.
        </p>
      </section>

      <section>
        <h2>6. Fazendo as Alterações</h2>
        <p>
          Depois do backup, você pode partir para a edição. O fluxo geral é:
        </p>
        <ol>
          <li>
            Escolha qual aba (<strong>Variants</strong> ou{" "}
            <strong>Custom</strong>) você vai alterar primeiro.
            <br />
            <em>
              Dica Importante: Ao escrever (“Write”) uma Variant, a parte
              “Custom” costuma ser resetada para o padrão de fábrica.
            </em>
            <br />
            Portanto:
            <ol type="a">
              <li>Primeiro edite/escreva a Variant.</li>
              <li>Depois edite/escreva a Custom.</li>
            </ol>
          </li>
          <li>
            Para editar:
            <ul>
              <li>Selecione Variants → Editor (ou “Edit”).</li>
              <li>Localize as linhas que deseja alterar.</li>
              <li>
                Após ajustar, clique em “Apply” (ou “Ok”) e então “Write”.
              </li>
              <li>
                Confirme que a ignição está ligada, clique em “OK” e aguarde. O
                painel do carro pode piscar/religar.
              </li>
            </ul>
          </li>
          <li>
            Em seguida, vá para Custom → Editor, faça suas alterações → “Apply”
            → “Write”.
          </li>
        </ol>

        <h3>6.1 Parâmetros mais comuns</h3>
        <p>
          Abaixo, alguns dos parâmetros mais buscados. Os nomes podem mudar
          ligeiramente dependendo da versão do MMCodingWriter, mas em geral
          seguem esse padrão:
        </p>
        <ul>
          <li>
            <strong>a) Travamento automático das portas (Speed Lock)</strong>
            <br />
            Em Variants, procure algo como:
            <br />
            - Auto Door Lock Mode, Door Lock by Speed, ou Auto Door Lock &gt;
            Crash & SpeedLock.
            <br />
            Mude de “Disabled” para “Enabled” ou “Crash + SpeedLock”.
            <br />
            Em Custom, confirme e habilite:
            <br />
            - Auto Door Lock by vehicle speed = Enable.
            <br />
            Isso fará o carro travar as portas ao atingir cerca de 20 km/h.
          </li>
          <li>
            <strong>b) Destravar as portas ao colocar em “P”</strong>
            <br />
            Em Custom ou Variants (dependendo do carro):
            <br />
            - Auto Door Unlock = Always SHIFT P Position ou algo similar.
            <br />
            Certifique-se de que não esteja “Disabled”.
          </li>
          <li>
            <strong>
              c) Desabilitar reset automático do computador de bordo (Trip)
            </strong>
            <br />
            Procure em Variants ou Custom algo como:
            <br />
            - Trip Auto Reset / Trip A/B Auto Reset.
            <br />
            Altere de “Enabled” (ou “Enable”) para “Disable”.
            <br />
            Em alguns Mitsubishis, depois de fazer essa alteração, é preciso ir
            nas configurações do computador de bordo (no painel do carro) e
            mudar de “Auto” para “Manual” na opção de reset da Trip.
          </li>
          <li>
            <strong>d) Buzina ao trancar (Horn Feedback / Horn at Lock)</strong>
            <br />
            Em Custom:
            <br />
            - Horn at Lock ou Horn Type at Lock.
            <br />
            Você pode escolher se quer “Short” (bip curto), “Long” (buzinada
            longa), “Double Click” (só buzina se apertar duas vezes), ou
            “Disable” (sem buzina).
          </li>
          <li>
            <strong>
              e) Subir vidros pelo controle (Power Window via Remote)
            </strong>
            <br />
            Em “Variants” ou “Custom”:
            <br />
            - Procure por algo como Power Window Type, Window Roll-Up by Remote,
            Multimode ou PW Customize.
            <br />
            Se estiver “Disabled”, selecione “Enabled/Present/Enable + Shift”
            etc.
            <br />
            Em alguns modelos, só fecha os vidros frontais; outros, todos os 4.
          </li>
          <li>
            <strong>f) Sensibilidade dos faróis automáticos</strong>
            <br />
            Em Custom (ou Variants dependendo da versão):
            <br />
            - Auto Light Sensitivity.
            <br />
            Normalmente existem 5 níveis (1 = mais sensível, 5 = menos
            sensível). Ao colocar “1”, o farol acende mais cedo (em pouca
            escuridão).
          </li>
          <li>
            <strong>g) Tempo das luzes internas (Interior Lamp)</strong>
            <br />
            - Interior Lamp Auto Cut Time ou Interior Delay Time.
            <br />
            Você pode colocar 15s, 30s, etc.
          </li>
        </ul>
      </section>

      <section>
        <h2>7. Gravando as Modificações</h2>
        <ol>
          <li>
            Em cada aba (Variants/Custom), após clicar em “Apply” ou “Ok”,
            clique em “Write” para enviar ao carro.
          </li>
          <li>O programa perguntará “Ignition is ON?” – confirme.</li>
          <li>
            A central ETACS reiniciará. Muitas vezes o painel pisca e o carro
            emite sons de relé.
          </li>
          <li>
            Assim que finalizar, feche a janela ou clique em “Disconnect”.
          </li>
        </ol>
        <p>
          <strong>Lembrete:</strong> Se você modificou “Variants” por último, a
          “Custom” pode ter sido resetada e vice-versa. O ideal é:
        </p>
        <ol type="a">
          <li>Ajustar e gravar “Variants” primeiro;</li>
          <li>Reconectar, ajustar e gravar “Custom” em seguida.</li>
        </ol>
      </section>

      <section>
        <h2>8. Testando no Carro</h2>
        <p>Após concluir as gravações, faça um teste:</p>
        <ul>
          <li>
            Auto Lock: saia dirigindo; a ~20 km/h, as portas devem travar.
          </li>
          <li>
            Auto Unlock: coloque o câmbio em “P” e verifique se as portas
            destravam.
          </li>
          <li>
            Trip: desligue e ligue o carro para ver se o computador de bordo não
            resetou.
          </li>
          <li>
            Buzina: trave o carro e confira se deu o bip curto/longo ou não,
            conforme configurado.
          </li>
          <li>
            Vidros: tente segurar o botão de travar no controle para ver se os
            vidros sobem.
          </li>
        </ul>
        <p>
          Caso algo não tenha dado certo, reconecte ao MMCodingWriter, verifique
          os parâmetros ou reescreva a variante/personalização.
        </p>
      </section>

      <section>
        <h2>9. Exemplos de Outras Funções</h2>
        <ul>
          <li>
            <strong>Farol de Neblina Traseiro (Rear Fog Light):</strong>
            <br />
            Em alguns Mitsubishis importados, essa opção existe mas pode vir
            desabilitada. Busque por Rear Fog Lamp / Rear Fog e coloque como
            “Present” ou “Enabled”, se seu chicote/botão suportar.
          </li>
          <li>
            <strong>Reset do Câmbio CVT:</strong>
            <br />O MMCodingWriter pode acessar o módulo do câmbio. Se você
            trocou o fluido do CVT, às vezes pode “zerar” o contador de “ATF
            Wear” ou efetuar um “CVT Learning”. Faça apenas se souber o que está
            fazendo.
          </li>
          <li>
            <strong>Teste do Painel (Meter Test):</strong>
            <br />
            Possível acionar todos os ponteiros e luzes de advertência para
            verificar se algo está queimado.
          </li>
        </ul>
      </section>

      <section>
        <h2>10. Solução de Problemas</h2>
        <ul>
          <li>
            <strong>Falha ao Conectar:</strong>
            <br />
            Tente desconectar o ELM327, desligar ignição, reconectar e ligar
            ignição. Verifique se a COM Port está correta e em 38400 bps. Alguns
            PCs precisam de repetidas tentativas.
          </li>
          <li>
            <strong>Não grava as alterações:</strong>
            <br />
            Certifique-se de ter marcado “Write Bypass” em Settings. Desmarque
            módulos que não esteja usando (Engine, ABS, etc.) e tente só com
            ETACS. Confira se a bateria do carro não está fraca.
          </li>
          <li>
            <strong>O carro não aceita certa função:</strong>
            <br />
            Pode ser que seu modelo/versão não suporte aquela função (por ex.,
            rebatimento elétrico de retrovisor em um modelo que não tem o motor
            de rebatimento).
          </li>
          <li>
            <strong>Alarmes/Falhas:</strong>
            <br />
            Use o MMCodingWriter na aba “DTC” para ler/apagar os códigos. Caso
            persista, retorne ao backup original ou procure especialista.
          </li>
        </ul>
      </section>

      <section>
        <h2>11. Conclusão</h2>
        <p>
          Com esse guia, você tem uma visão geral de como comunicar seu notebook
          com a central ETACS da Mitsubishi e personalizar diversas funções de
          conforto e conveniência. As etapas-chave são:
        </p>
        <ol>
          <li>
            Ter a interface correta (ELM327 v1.5 verdadeiro ou mini VCI J2534).
          </li>
          <li>Configurar Bluetooth/USB e portas COM em 38400.</li>
          <li>Instalar e executar o MMCodingWriter, conectando em “ETACS”.</li>
          <li>Fazer backup do Variant e Custom originais.</li>
          <li>
            Modificar os parâmetros desejados (Variant primeiro, Custom em
            seguida).
          </li>
          <li>Gravar (“Write”) e testar no carro.</li>
        </ol>
        <p>
          Lembre-se sempre de fazer backup antes de qualquer mudança e de ler
          cuidadosamente cada parâmetro que for alterar. Em muitos casos, o
          procedimento é simples e dá ao proprietário do Mitsubishi recursos que
          antes vinham desabilitados.
        </p>
        <p>
          Boa sorte na programação e aproveite as novas funcionalidades! Se
          surgirem dúvidas ou problemas mais complexos, procure ajuda em fóruns
          especializados ou em profissionais que conheçam bem a linha
          Mitsubishi.
        </p>

        <hr />

        <p>
          <strong>Observação Legal:</strong>
        </p>
        <p>
          Este é um conteúdo meramente informativo, sem garantia de
          funcionamento em todos os modelos/versões de Mitsubishi. Alterações
          indevidas podem causar falhas no veículo. Sempre faça backup e realize
          cada passo por sua conta e risco.
        </p>
      </section>

      <hr className="my-4" />

      <section>
        <h3>Vídeo Tutorial (YouTube)</h3>
        <div className="ratio ratio-16x9 mb-3">
          <iframe
            src="https://www.youtube.com/embed/mRnJ7LbBpTs?rel=0"
            title="Tutorial ETACS - MMCodingWriter"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
        <p>Confira o passo a passo em vídeo para reforçar o aprendizado.</p>
      </section>

      <AdSenseBanner />
    </article>
  );
}

export default Tutorial;
